import { HttpClient, HttpContext, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { shareReplay, startWith, Subject, switchMap, tap } from "rxjs";
import { Aircraft, Flight } from "../models";

export interface FlightSearchQuery {
  startDate?: string | null; // "YYYY-MM-DD"
  endDate?: string | null;
  aircraftCodes?: string[];
  page: number;
  size: number;
  sort: string; // ex: "date"
  dir: "asc" | "desc";
}

export interface PagedResult<T> {
  items: T[];
  total: number;
}

@Injectable({ providedIn: "root" })
export class FlightService {
  private readonly base = window.location.port === "4200" ? "/api" : "/api"; // dev et prod via /api

  private refresh$ = new Subject<void>();

  // flux public de la liste
  readonly flights$ = this.refresh$.pipe(
    startWith(void 0), // premier chargement
    switchMap(() => this.http.get<Flight[]>(`${this.base}/flights`)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(private http: HttpClient) {}

  searchFlights(query: FlightSearchQuery) {
    let params = new HttpParams()
      .set("page", query.page)
      .set("size", query.size)
      .set("sort", query.sort)
      .set("dir", query.dir);

    if (query.startDate) params = params.set("startDate", query.startDate);
    if (query.endDate) params = params.set("endDate", query.endDate);
    if (query.aircraftCodes && query.aircraftCodes.length) {
      params = params.set("aircraftCodes", query.aircraftCodes.join(","));
    }

    return this.http.get<PagedResult<any>>(`${this.base}/flight`, { params });
  }

  createFlight(dto: any, opts?: { context?: HttpContext }) {
    return this.http.post(`${this.base}/flight`, dto, {
      context: opts?.context,
    });
  }

  update(id: string, changes: Partial<Flight>) {
    return this.http.put<Flight>(`${this.base}/flight/${id}`, changes).pipe(
      tap(() => this.refresh$.next()) // déclenche le rechargement
    );
  }

  remove(id: string) {
    return this.http
      .delete(`${this.base}/flight/${id}`)
      .pipe(tap(() => this.refresh$.next()));
  }
}
